/**
 * I18n Context
 *
 * Provider de internacionalizacao para toda a aplicacao React.
 * Wrapper sobre react-i18next com funcionalidades extras para TMF.
 *
 * @since 2025-12-16
 * @epic INIT-0045-03
 *
 * @example
 * // No App.tsx
 * import { I18nProvider } from '@/i18n';
 * <I18nProvider>
 *   <App />
 * </I18nProvider>
 *
 * @example
 * // Em componentes
 * import { useTmfTranslation } from '@/i18n';
 * const { t, language, changeLanguage, languages } = useTmfTranslation();
 * return (
 *   <div>
 *     <select onChange={(e) => changeLanguage(e.target.value)}>
 *       {languages.map(l => <option key={l.code}>{l.name}</option>)}
 *     </select>
 *     <h1>{t('welcome')}</h1>
 *   </div>
 * );
 */

import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/i18n/config';
import { useTmfTranslation, useDateFormat, useNumberFormat } from '@/i18n/hooks';
import { supportedLanguages, type SupportedLanguageCode } from '@/i18n/config';
import type { Language, TranslationResponse } from '@/types/i18n';

// ==========================================
// TYPES (compatibilidade com API antiga)
// ==========================================

export interface I18nContextValue {
  // Estado
  language: string;
  languages: Language[];
  isLoading: boolean;
  isInitialized: boolean;
  error: Error | null;

  // Acoes
  setLanguage: (code: string) => void;
  t: (key: string, params?: Record<string, string | number>, defaultValue?: string) => string;
  loadMessages: (category: string) => Promise<void>;
  loadTranslation: (model: string, modelId: number, field?: string) => Promise<TranslationResponse>;

  // Helpers
  formatDate: (date: Date | string, format?: string) => string;
  formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (value: number, currency?: string) => string;
}

// ==========================================
// CONTEXT (para compatibilidade)
// ==========================================

const I18nContext = createContext<I18nContextValue | null>(null);

// ==========================================
// PROVIDER
// ==========================================

interface I18nProviderProps {
  children: ReactNode;
  /** Idioma inicial (opcional, detectado automaticamente) */
  initialLanguage?: string;
  /** Namespaces para pre-carregar (alias: preloadCategories) */
  preloadNamespaces?: string[];
  /** Categorias para pre-carregar (alias para preloadNamespaces) */
  preloadCategories?: string[];
}

/**
 * Provider de i18n - Agora usa react-i18next
 */
export function I18nProvider({
  children,
  initialLanguage,
  preloadNamespaces,
  preloadCategories,
}: I18nProviderProps) {
  // Usar preloadCategories como alias para preloadNamespaces
  const namespaces = preloadNamespaces || preloadCategories || ['common', 'buttons', 'errors'];

  // Configurar idioma inicial se especificado
  if (initialLanguage && i18n.language !== initialLanguage) {
    i18n.changeLanguage(initialLanguage);
  }

  return (
    <I18nextProvider i18n={i18n}>
      <I18nContextProvider preloadNamespaces={namespaces}>
        {children}
      </I18nContextProvider>
    </I18nextProvider>
  );
}

// ==========================================
// CONTEXT PROVIDER (interno)
// ==========================================

function I18nContextProvider({
  children,
  preloadNamespaces,
}: {
  children: ReactNode;
  preloadNamespaces: string[];
}) {
  const {
    t,
    language,
    changeLanguage,
    isLoading,
    isReady,
    i18n: i18nInstance,
  } = useTmfTranslation(preloadNamespaces);

  const { formatDate: formatDateFn, formatDateTime } = useDateFormat();
  const { formatNumber: formatNumberFn, formatCurrency: formatCurrencyFn } = useNumberFormat();

  // Converter para formato de Language antigo
  const languages: Language[] = supportedLanguages.map((lang, index) => ({
    id: index + 1,
    language_name: lang.name,
    language_code: lang.code.replace('-', '_'), // pt-BR -> pt_BR
    locale: lang.code,
    status: 'active',
    is_default: lang.code === 'pt-BR',
  }));

  // Handler de mudanca de idioma
  const setLanguage = async (code: string) => {
    // Converter formato antigo para novo se necessario
    const newCode = code.replace('_', '-') as SupportedLanguageCode;
    await changeLanguage(newCode);
  };

  // Carregar mensagens (agora via loadNamespaces do i18next)
  const loadMessages = async (category: string): Promise<void> => {
    await i18nInstance.loadNamespaces(category);
  };

  // Load translation (para traducoes de modelo - API Python)
  const loadTranslation = async (
    model: string,
    modelId: number,
    _field?: string
  ): Promise<TranslationResponse> => {
    // TODO: Integrar com API Python de traducoes de modelo
    if (import.meta.env.DEV) console.warn('[i18n] loadTranslation: TODO implementar integracao com API Python');
    return {
      model,
      model_id: modelId,
      language_code: language,
      translations: [],
      fallback_used: false,
    };
  };

  // Formatadores com conversao de formato
  const formatDate = (date: Date | string, format?: string): string => {
    if (format === 'datetime') {
      return formatDateTime(date);
    }
    return formatDateFn(date, format ? { dateStyle: format as 'short' | 'long' } : undefined);
  };

  const formatNumber = (num: number, options?: Intl.NumberFormatOptions): string => {
    return formatNumberFn(num, options);
  };

  const formatCurrency = (value: number, currency: string = 'BRL'): string => {
    return formatCurrencyFn(value, currency);
  };

  // Value do contexto
  const value: I18nContextValue = {
    language: language.replace('-', '_'), // pt-BR -> pt_BR (compatibilidade)
    languages,
    isLoading,
    isInitialized: isReady,
    error: null,
    setLanguage,
    t,
    loadMessages,
    loadTranslation,
    formatDate,
    formatNumber,
    formatCurrency,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

// ==========================================
// HOOK (compatibilidade com API antiga)
// ==========================================

/**
 * Hook para acessar o contexto de i18n (compatibilidade)
 *
 * @deprecated Prefira usar useTmfTranslation de '@/i18n'
 */
export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n deve ser usado dentro de um I18nProvider');
  }
  return context;
}

// ==========================================
// LANGUAGE SELECTOR (compatibilidade)
// ==========================================

interface LanguageSelectorProps {
  className?: string;
  showFlag?: boolean;
  showName?: boolean;
}

/**
 * Componente seletor de idioma (compatibilidade)
 *
 * @deprecated Prefira usar LanguageSelector de '@/i18n'
 */
export function LanguageSelector({
  className = '',
  showFlag = true,
  showName = true,
}: LanguageSelectorProps) {
  const { language, languages, setLanguage } = useI18n();

  // Mapa de bandeiras por codigo de idioma
  const flagMap: Record<string, string> = {
    pt_BR: '\u{1F1E7}\u{1F1F7}',
    en_US: '\u{1F1FA}\u{1F1F8}',
    es_ES: '\u{1F1EA}\u{1F1F8}',
    fr_FR: '\u{1F1EB}\u{1F1F7}',
    ja_JP: '\u{1F1EF}\u{1F1F5}',
    zh_CN: '\u{1F1E8}\u{1F1F3}',
    de_DE: '\u{1F1E9}\u{1F1EA}',
  };

  if (languages.length <= 1) {
    return null;
  }

  return (
    <select
      value={language}
      onChange={(e) => setLanguage(e.target.value)}
      className={`px-2 py-1 border rounded ${className}`}
      aria-label="Selecionar idioma"
    >
      {languages.map((lang) => (
        <option key={lang.language_code} value={lang.language_code}>
          {showFlag && flagMap[lang.language_code] ? `${flagMap[lang.language_code]} ` : ''}
          {showName ? lang.language_name : lang.language_code}
        </option>
      ))}
    </select>
  );
}

// ==========================================
// TRANS COMPONENT (compatibilidade)
// ==========================================

interface TransProps {
  /** Chave de traducao */
  i18nKey: string;
  /** Parametros para interpolacao */
  params?: Record<string, string | number>;
  /** Valor padrao se traducao nao encontrada */
  defaultValue?: string;
  /** Tag HTML a usar (default: span) */
  as?: keyof JSX.IntrinsicElements;
  /** Props adicionais para o elemento */
  [key: string]: unknown;
}

/**
 * Componente para traducao inline (compatibilidade)
 *
 * @deprecated Prefira usar T de '@/i18n'
 */
export function Trans({
  i18nKey,
  params,
  defaultValue,
  as: Tag = 'span',
  ...props
}: TransProps) {
  const { t } = useI18n();
  return <Tag {...props}>{t(i18nKey, params, defaultValue)}</Tag>;
}

// ==========================================
// EXPORTS
// ==========================================

export default I18nContext;
